import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, switchMap, throwError } from 'rxjs';

interface SeoApiErrorBody {
  error_code?: string;
  error_message?: string;
  detail?: string;
  status?: number;
  request_id?: string;
}

export const INVALID_URL_VALIDATION_MESSAGE = 'Analysis cannot be performed on the entered URL. Please select a valid URL from the autocomplete suggestions before running the analysis.';

export class SeoApiError extends Error {
  override name = 'SeoApiError';

  constructor(
    message: string,
    readonly status: number,
    readonly errorCode?: string,
    readonly backendMessage?: string,
    readonly requestId?: string,
  ) {
    super(message);
  }

  get isInvalidUrlValidation(): boolean {
    const message = (this.backendMessage ?? this.message).toLowerCase();
    return this.status === 422 && this.errorCode === 'VALIDATION_ERROR' && message.includes('invalid url');
  }
}

export function isInvalidUrlValidationError(error: unknown): error is SeoApiError {
  return error instanceof SeoApiError && error.isInvalidUrlValidation;
}

@Injectable({
  providedIn: 'root',
})
export class SeoApiErrorService {
  handle(error: unknown, fallbackMessage: string): Observable<never> {
    if (error instanceof HttpErrorResponse && error.error instanceof Blob) {
      return from(this.parseErrorBlob(error.error)).pipe(
        switchMap((body) => throwError(() => this.buildError(error, fallbackMessage, body)))
      );
    }

    return throwError(() => this.buildError(error, fallbackMessage));
  }

  private buildError(error: unknown, fallbackMessage: string, parsedBody?: SeoApiErrorBody | null): Error {
    if (!(error instanceof HttpErrorResponse)) {
      const message = error instanceof Error && error.message ? error.message : fallbackMessage;
      return new Error(message);
    }

    const body = parsedBody ?? this.normalizeErrorBody(error.error);
    const backendMessage = this.cleanMessage(body?.error_message ?? body?.detail);
    const status = body?.status ?? error.status;
    const message = this.messageFor(backendMessage, fallbackMessage, error, status);

    return new SeoApiError(message, status, body?.error_code, backendMessage ?? undefined, body?.request_id);
  }

  private messageFor(
    backendMessage: string | null,
    fallbackMessage: string,
    error: HttpErrorResponse,
    status: number
  ): string {
    if (error.status === 0) {
      return 'Unable to reach the SEO service. Please check your connection and try again.';
    }

    return this.withStatus(backendMessage ?? fallbackMessage, status);
  }

  private async parseErrorBlob(blob: Blob): Promise<SeoApiErrorBody | null> {
    if (blob.size === 0) return null;

    try {
      return this.normalizeErrorBody(await blob.text());
    } catch {
      return null;
    }
  }

  private normalizeErrorBody(raw: unknown): SeoApiErrorBody | null {
    if (!raw) return null;

    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      if (!trimmed) return null;

      try {
        return this.normalizeErrorBody(JSON.parse(trimmed));
      } catch {
        return { error_message: trimmed };
      }
    }

    if (!this.isRecord(raw)) return null;

    return {
      error_code: this.stringValue(raw['error_code']),
      error_message: this.stringValue(raw['error_message']),
      detail: this.stringValue(raw['detail']),
      status: this.numberValue(raw['status']),
      request_id: this.stringValue(raw['request_id']),
    };
  }

  private stringValue(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
  }

  private numberValue(value: unknown): number | undefined {
    return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
  }

  private cleanMessage(value: string | undefined): string | null {
    return value?.trim() || null;
  }

  private withStatus(message: string, status: number): string {
    return status > 0 ? `${message} (Status: ${status})` : message;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }
}
